import { useContext, useEffect, useState, type FC } from "react";
import CurrencyInput from "react-currency-input-field";
import {
  Button,
  Col,
  Divider,
  Form,
  Grid,
  Input,
  Modal,
  Row,
  SelectPicker,
  Toggle,
} from "rsuite";
import MainLayout from "../components/MainLayout";
import { LoadingContext } from "../contexts/LoadingContext";
import { Account } from "../models/finance/account";
import { Role } from "../models/system/role";
import { PaymentFee, Setting } from "../models/system/setting";
import { getAccounts } from "../services/finance/AccountApi";
import {
  createPaymentFee,
  deletePaymentFeeById,
  getBankCode,
  getEWalletCode,
  getRoles,
  getSetting,
  getVACode,
  updateSetting,
} from "../services/inventory/SystemApi";
import { errorToast, successToast } from "../utils/helper-ui";
import { BsTrash3 } from "react-icons/bs";
import { money } from "../utils/helper";

interface SettingPageProps {}

const SettingPage: FC<SettingPageProps> = ({}) => {
  const [mounted, setMounted] = useState(false);
  const [setting, setSetting] = useState<Setting>();
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [roles, setRoles] = useState<Role[]>([]);
  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);
  const [saleAccounts, setSaleAccounts] = useState<Account[]>([]);
  const [bankCodes, setBankCodes] = useState<{ name: string; code: string }[]>(
    []
  );
  const [vaCodes, setVACodes] = useState<{ name: string; code: string }[]>([]);
  const [ewallets, setEwallets] = useState<{ name: string; code: string }[]>(
    []
  );
  const [showPaymentFeeModal, setShowPaymentFeeModal] = useState(false);
  const [activePaymentFee, setActivePaymentFee] = useState<PaymentFee>();
  const [paymentFees, setPaymentFees] = useState<PaymentFee[]>([]);
  const [paymentFeeType, setPaymentFeeType] = useState<
    { label: string; value: string }[]
  >([
    { label: "Virtual Account", value: "VA" },
    { label: "E-Wallet", value: "E-WALLET" },
  ]);
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    getSetting()
      .then((v) => v.json())
      .then((res) => {
        setSetting(res.data);
      });
    getRoles({ page: 1, size: 10, search: "" }, "merchant")
      .then((v) => v.json())
      .then((res) => {
        setRoles(res.data.items);
      });

    getBankCode()
      .then((v) => v.json())
      .then((res) => {
        setBankCodes(res.data);
      });

    getVACode()
      .then((v) => v.json())
      .then((res) => {
        setVACodes(res.data);
      });

    getEWalletCode()
      .then((v) => v.json())
      .then((res) => {
        setEwallets(res.data);
      });

    getAccounts({ page: 1, size: 10, search: "", type: "ASSET" })
      .then((v) => v.json())
      .then((res) => {
        setAssetAccounts(res.data.items);
      });
    getAccounts({ page: 1, size: 10, search: "", type: "REVENUE" })
      .then((v) => v.json())
      .then((res) => {
        setSaleAccounts(res.data.items);
      });
  }, [mounted]);

  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Setting</h1>
      <Button
        loading={isLoading}
        className="w-36"
        appearance="primary"
        onClick={() => {
          setIsLoading(true);
          updateSetting(setting!)
            .then((v) => v.json())
            .then((res) => {
              getSetting();
              successToast(res.message);
            })
            .catch(errorToast)
            .finally(() => {
              setIsLoading(false);
            });
        }}
      >
        Save
      </Button>
    </div>
  );

  const addPaymentFee = async () => {
    try {
      setIsLoading(true);
      const res = await createPaymentFee(activePaymentFee!);
      const data = await res.json();
      setShowPaymentFeeModal(false);
      getSetting()
        .then((v) => v.json())
        .then((res) => {
          setSetting(res.data);
        });
    } catch (error) {
      errorToast(error);
    } finally {
      setShowPaymentFeeModal(false);
      setIsLoading(false);
    }
  };
  const deletePaymentFee = async (data: PaymentFee) => {
    try {
      if (
        window.confirm(
          `Are you sure you want to delete payment fee with id ${data.name}?`
        )
      ) {
        setIsLoading(true);
        await deletePaymentFeeById(data.id!);
        getSetting()
          .then((v) => v.json())
          .then((res) => {
            setSetting(res.data);
          });
      }
    } catch (error) {
      errorToast(error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <MainLayout header={renderHeader()}>
      <Grid fluid>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">App Name</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="App Name"
              value={setting?.app_name ?? ""}
              onChange={(v) => setSetting({ ...setting!, app_name: v })}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Phone</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Phone"
              value={setting?.phone ?? ""}
              onChange={(v) => setSetting({ ...setting!, phone: v })}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Email</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Email"
              value={setting?.email ?? ""}
              onChange={(v) => setSetting({ ...setting!, email: v })}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Address</span>
          </Col>
          <Col md={16}>
            <Input
              as={"textarea"}
              rows={5}
              placeholder="Address"
              value={setting?.address ?? ""}
              onChange={(v) => setSetting({ ...setting!, address: v })}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Backoffice Version</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Backoffice Version"
              value={setting?.backoffice_version ?? ""}
              onChange={(v) =>
                setSetting({ ...setting!, backoffice_version: v })
              }
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Merchant App Version</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Merchant App Version"
              value={setting?.merchant_app_version ?? ""}
              onChange={(v) =>
                setSetting({ ...setting!, merchant_app_version: v })
              }
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">User App Version</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="User App Version"
              value={setting?.user_app_version ?? ""}
              onChange={(v) => setSetting({ ...setting!, user_app_version: v })}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">
              Email Approval Recipient
            </span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Email Approval Recipient"
              value={setting?.email_approval_recipient ?? ""}
              onChange={(v) =>
                setSetting({ ...setting!, email_approval_recipient: v })
              }
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">
              Whatsapp Approval Recipient
            </span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Whatsapp Approval Recipient"
              value={setting?.whatsapp_approval_recipient ?? ""}
              onChange={(v) =>
                setSetting({ ...setting!, whatsapp_approval_recipient: v })
              }
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Default Merchant Role</span>
          </Col>
          <Col md={16}>
            <SelectPicker
              block
              data={roles}
              labelKey="name"
              valueKey="id"
              placeholder="Default Merchant Role"
              value={setting?.default_role_id ?? ""}
              onChange={(v) => setSetting({ ...setting!, default_role_id: v! })}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Distance Range</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Distance Range"
              value={setting?.distance_range ?? ""}
              onChange={(v) => setSetting({ ...setting!, distance_range: v })}
            />
            <small className="italic text-gray-400">
              Seperate by comma (,)
            </small>
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Service Fee</span>
          </Col>
          <Col md={16}>
            <CurrencyInput
              className="rs-input"
              value={setting?.service_fee ?? 0}
              groupSeparator="."
              decimalSeparator=","
              onValueChange={(value, name, values) => {
                setSetting({ ...setting!, service_fee: values?.float ?? 0 });
              }}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Tax(%)</span>
          </Col>
          <Col md={16}>
            <CurrencyInput
              className="rs-input"
              value={setting?.tax ?? 0}
              groupSeparator="."
              decimalSeparator=","
              onValueChange={(value, name, values) => {
                setSetting({ ...setting!, tax: values?.float ?? 0 });
              }}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Tax Type</span>
          </Col>
          <Col md={16}>
            <SelectPicker
              block
              data={[
                { label: "Included", value: "INCLUDED" },
                { label: "Excluded", value: "EXCLUDED" },
              ]}
              placeholder="Tax Type"
              value={setting?.tax_type ?? ""}
              onChange={(v) => setSetting({ ...setting!, tax_type: v! })}
            />
            <small className="italic text-gray-400">
              Include: Total price is the price of the product plus the tax.
              Exclude: Total price is the price of the product, and the tax is
              additional.
            </small>
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Recipient Bank</span>
          </Col>
          <Col md={16}>
            <SelectPicker
              block
              data={bankCodes}
              labelKey="name"
              valueKey="code"
              placeholder="Recipient Bank"
              value={setting?.recipient_bank ?? ""}
              onChange={(v) => setSetting({ ...setting!, recipient_bank: v! })}
            />
          </Col>
        </Row>

        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Recipient Account</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Recipient Account"
              value={setting?.recipient_account ?? ""}
              onChange={(v) =>
                setSetting({ ...setting!, recipient_account: v! })
              }
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">
              Recipient Account Name
            </span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Recipient Account Name"
              value={setting?.recipient_account_name ?? ""}
              onChange={(v) =>
                setSetting({ ...setting!, recipient_account_name: v! })
              }
            />
          </Col>
        </Row>

        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Cash / Bank Account</span>
          </Col>
          <Col md={16}>
            <SelectPicker
              block
              data={assetAccounts}
              labelKey="name"
              valueKey="id"
              placeholder="Cash / Bank Account"
              value={setting?.asset_account_id ?? ""}
              onChange={(v) =>
                setSetting({ ...setting!, asset_account_id: v! })
              }
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Sales Account</span>
          </Col>
          <Col md={16}>
            <SelectPicker
              block
              data={saleAccounts}
              labelKey="name"
              valueKey="id"
              placeholder="Sales Account"
              value={setting?.sale_account_id ?? ""}
              onChange={(v) => setSetting({ ...setting!, sale_account_id: v! })}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Wa Support Number</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Wa Support Number"
              value={setting?.wa_support_number ?? ""}
              onChange={(v) =>
                setSetting({ ...setting!, wa_support_number: v })
              }
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Wa Support Message</span>
          </Col>
          <Col md={16}>
            <Input
              as={"textarea"}
              placeholder="Wa Support Message"
              value={setting?.wa_support_message ?? ""}
              onChange={(v) =>
                setSetting({ ...setting!, wa_support_message: v })
              }
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Wa Call Link</span>
          </Col>
          <Col md={16}>
            <Input
              placeholder="Wa Call Link"
              value={setting?.wa_call_link ?? ""}
              onChange={(v) => setSetting({ ...setting!, wa_call_link: v })}
            />
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Funds Held Period</span>
          </Col>
          <Col md={16}>
            <div className="relative">
              <Input
                type="number"
                placeholder="Funds Held Period"
                value={setting?.funds_held_period ?? ""}
                onChange={(v) =>
                  setSetting({ ...setting!, funds_held_period: parseInt(v) })
                }
              />
              <span className="absolute top-1/2 text-xs right-4 translate-y-[-50%]">
                Day
                </span>
            </div>
          </Col>
        </Row>
        <Row className="show-grid mb-4">
          <Col md={8}>
            <span className="text-normal font-bold">Auto Publish Feedback</span>
          </Col>
          <Col md={16}>
            <Toggle
              checked={setting?.auto_publish_feedback ?? false}
              onChange={(v) =>
                setSetting({ ...setting!, auto_publish_feedback: v })
              }
            />
          </Col>
        </Row>
        <Divider />
        <div className="flex flex-row justify-between mt-4">
          <h3 className="font-bold text-lg">Payment Fee</h3>
          <Button
            size="sm"
            appearance="primary"
            color="green"
            onClick={() => {
              setShowPaymentFeeModal(true);
              setActivePaymentFee({
                setting_id: setting!.id,
                type: "VA",
                amount: 0,
                amount_percent: 0,
                code: "",
              });
            }}
          >
            + Payment Fee
          </Button>
        </div>
        {(setting?.payment_fees ?? []).length === 0 ? (
          <small>
            No payment fees set for this setting. Click the button above to add
            one.
          </small>
        ) : (
          <table className="w-full border border-gray-300 mt-4">
            <thead>
              <tr>
                <th className="px-2 py-1 text-xs border text-left border-gray-300">
                  Name
                </th>
                <th className="px-2 py-1 text-xs border border-gray-300">
                  Type
                </th>
                <th className="px-2 py-1 text-xs border border-gray-300">
                  Payment Fee
                </th>
                <th className="px-2 py-1 text-xs border border-gray-300">
                  Payment Fee(%)
                </th>
                <th className="px-2 py-1 text-xs border border-gray-300">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {(setting?.payment_fees ?? []).map((paymentFee, index) => (
                <tr key={index}>
                  <td className="px-2 py-1 text-xs border border-gray-300">
                    {paymentFee.name}
                  </td>
                  <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                    {paymentFee.type}
                  </td>
                  <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                    {money(paymentFee.amount)}
                  </td>
                  <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                    {money(paymentFee.amount_percent)}%
                  </td>
                  <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                    <div className="flex flex-row justify-center">
                      <BsTrash3
                        className="text-red-500 hover:text-red-800 cursor-pointer"
                        size={16}
                        onClick={() => deletePaymentFee(paymentFee!)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Grid>

      <Modal
        size={"md"}
        open={showPaymentFeeModal}
        onClose={() => {
          setShowPaymentFeeModal(false);
          setActivePaymentFee(undefined);
        }}
      >
        <Modal.Header>
          <Modal.Title>Create Payment Fee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="w-full" layout="horizontal" fluid>
            <div className="form-group">
              <label>Type</label>
              <div className="input">
                <SelectPicker
                  block
                  data={paymentFeeType}
                  value={activePaymentFee?.type}
                  onChange={(val) => {
                    setActivePaymentFee({ ...activePaymentFee!, type: val! });
                  }}
                  placeholder="Select Type"
                />
              </div>
            </div>
            {activePaymentFee?.type === "VA" && (
              <div className="form-group">
                <label>Bank</label>
                <div className="input">
                  <SelectPicker
                    block
                    data={vaCodes}
                    labelKey="name"
                    valueKey="code"
                    value={activePaymentFee?.code}
                    onChange={(val) => {
                      setActivePaymentFee({ ...activePaymentFee!, code: val! });
                    }}
                    placeholder="Select Bank"
                  />
                </div>
              </div>
            )}
            {activePaymentFee?.type === "E-WALLET" && (
              <div className="form-group">
                <label>E-Wallet</label>
                <div className="input">
                  <SelectPicker
                    block
                    data={ewallets}
                    labelKey="name"
                    valueKey="code"
                    value={activePaymentFee?.code}
                    onChange={(val) => {
                      setActivePaymentFee({ ...activePaymentFee!, code: val! });
                    }}
                    placeholder="Select E-Wallet"
                  />
                </div>
              </div>
            )}
            <div className="form-group">
              <label>Payment Fee </label>
              <div className="input">
                <CurrencyInput
                  className="rs-input"
                  value={activePaymentFee?.amount ?? 0}
                  onValueChange={(value, name, values) => {
                    setActivePaymentFee({
                      ...activePaymentFee!,
                      amount: values?.float ?? 0,
                    });
                  }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Payment Fee (%)</label>
              <div className="input">
                <CurrencyInput
                  className="rs-input"
                  value={activePaymentFee?.amount_percent ?? 0}
                  decimalScale={2}
                  onValueChange={(value, name, values) => {
                    if ((values?.float ?? 0) > 100) {
                      setActivePaymentFee({
                        ...activePaymentFee!,
                        amount_percent: 100,
                      });
                    } else {
                      setActivePaymentFee({
                        ...activePaymentFee!,
                        amount_percent: values?.float ?? 0,
                      });
                    }
                  }}
                />
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={addPaymentFee} appearance="primary">
            Save & Close
          </Button>
        </Modal.Footer>
      </Modal>
    </MainLayout>
  );
};
export default SettingPage;
